import { useEffect, useRef, ReactNode } from 'react';
import { ExportPngInfo } from '@ecosystem/newron-client-collections';
import { useExportImages } from '@hooks/export/UseExportChartImage';
import { processExportedImages } from '@hooks/export/ExportChart';
import { generateUniqueChartKeys } from '@data-visualization/utilities/GenerateUniqueChartKeys';
import { useFetchChartData } from '@utils/collectionUtils/hooks/UseFetchChartData';
import { getBaseTimePeriodForMATAndYTD, getPeriodicityForMATAndYTD, getTrimmedMonthName } from '@utils/PeriodUtils';
import { getIsUsingCollectionLiveData, getPeriodLabel } from '@utils/collectionUtils/CollectionUtils';
import { getCountryCodes } from '@utils/CountryUtils';
import { PageType } from '@utils/pageConfig/Types';
import { ChartKeys } from '@const/ChartKeys';
import CountryWarningBanner from '@components/collections/warning-banner/country-warning-banner/CountryWarningBanner';
import { WarningBannerProps } from '@components/collections/warning-banner/Types';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import PgWarningBanner from '@components/collections/warning-banner/pg-warning-banner/PgWarningBanner';
import { getRequireAllPgLoadedInCollections } from '@components/collections/CollectionsFlagsByInsightType';
import { CollectionInsightType } from '@components/collections/Model';

interface UseLineChartInsightParams<T> {
  apiParams: any;
  dataType: string;
  period: string;
  pageType: PageType;
  marketChartKey: string;
  retailerChartKey: string;
  collectionInsightType: CollectionInsightType;
  onLoaded: () => void;
  onExportReady: (exports: { exportPng: () => Promise<ExportPngInfo> }) => void;
  apiRequest: (params: { getPeriodFromLabel: string; isUsingCollectionLiveData: boolean }) => Promise<T>;
  processMarketData: (data: T | null) => any;
  processRetailerData: (data: T | null) => any;
}

export const useLineChartInsight = <T,>({
  apiParams,
  dataType,
  period,
  pageType,
  marketChartKey,
  retailerChartKey,
  collectionInsightType,
  onLoaded,
  onExportReady,
  apiRequest,
  processMarketData,
  processRetailerData,
}: UseLineChartInsightParams<T>) => {
  const { exportImages } = useExportImages(pageType);
  const exportProcessed = useRef(false);
  const baseTimePeriod = getBaseTimePeriodForMATAndYTD(period, apiParams.periodType);
  const { periodicity } = getPeriodicityForMATAndYTD(baseTimePeriod, apiParams.periodType);

  const { isLoading, chartData } = useFetchChartData({
    apiRequest: async () => {
      const label = await getPeriodLabel({
        clientId: apiParams.clientId,
        periodicity,
        baseTimePeriod,
        comparisonPeriod: apiParams.comparisonPeriod,
        countryIds: getCountryCodes(apiParams.countries),
        productType: apiParams.productType,
        collectionDataType: getIsUsingCollectionLiveData(period, dataType) ? 'live' : (dataType as any),
        productGroups: apiParams.productGroup,
      });
      const getPeriodFromLabel = RegExp(/\b\w{3,9} \d{4}(?= vs)/i).exec(label)?.[0] ?? period;
      const isUsingCollectionLiveData = getIsUsingCollectionLiveData(getPeriodFromLabel, dataType);

      return apiRequest({ getPeriodFromLabel, isUsingCollectionLiveData });
    },
    onLoaded: onLoaded,
  });

  const marketData = processMarketData(chartData ?? null);
  const retailerData = processRetailerData(chartData ?? null);

  const chartImages = [
    exportImages[generateUniqueChartKeys(marketData, ChartKeys[pageType][marketChartKey])],
    exportImages[generateUniqueChartKeys(retailerData, ChartKeys[pageType][retailerChartKey])],
  ];

  useEffect(() => {
    const exportImage = async () => {
      if (chartImages.length && !chartImages.some((image) => image === undefined) && !exportProcessed.current) {
        const processedImages = await processExportedImages(chartImages);
        exportProcessed.current = true;
        const exportPowerPointImages = async (): Promise<ExportPngInfo> => {
          return { images: processedImages };
        };
        onExportReady({ exportPng: exportPowerPointImages });
      }
    };
    exportImage();
  }, [chartImages, onExportReady]);

  const getWarningBannerWrapper = (children: ReactNode) => {
    const { clientId, period, periodType, countries, productGroup, productType } = apiParams;
    
    const warningBannerProps: WarningBannerProps = {
      clientId: clientId,
      countries: countries.map(({ id }: { id: string }) => ({ value: id, label: id })),
      productGroups: productGroup ?? [],
      period: period,
      periodicity: periodType,
      comparisonPeriod: RelativePeriod.PreviousYearCurrentPeriod,
      productType,
      collectionDataType: 'live',
    };

    const isSelectSingleCountry = countries?.length === 1;
    const requireAllPgLoadedInCollections = getRequireAllPgLoadedInCollections(collectionInsightType);

    if (isSelectSingleCountry) {
      const {productsParam, ...filteredApiParams} = apiParams
       filteredApiParams.period = getTrimmedMonthName(filteredApiParams.period);
      return (
        <PgWarningBanner
          baseApiParams={filteredApiParams}
          collectionDataType={dataType}
          requireAllPgLoadedInCollections={requireAllPgLoadedInCollections}
        >
          {children}
        </PgWarningBanner>
      );
    }

    return <CountryWarningBanner warningBannerProps={warningBannerProps}>{children}</CountryWarningBanner>;
  };

  return {
    isLoading,
    chartData,
    getWarningBannerWrapper,
  };
};